// Per-biome colour helpers for the terrain tiles — tints, season blending
// and the packed base colours consumed by the edge-attribute builder. Split
// out of TerrainTiles.tsx.

import type { SceneNode } from '@aod/protocol';
import * as THREE from 'three';
import { hexNoise, smoothField } from './elevation.js';
import { FROST_COLORS, type SeasonTint, applySeasonTint } from './seasons.js';
import { packRGB } from './terrainEdges.js';

const HEX_SIZE = 1;
const _color = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };
export function makeHexShape(): THREE.Shape {
  const shape = new THREE.Shape();
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (30 + 60 * i);
    const x = HEX_SIZE * Math.cos(angle);
    const z = HEX_SIZE * Math.sin(angle);
    if (i === 0) shape.moveTo(x, z);
    else shape.lineTo(x, z);
  }
  shape.closePath();
  return shape;
}

export const hexShape = makeHexShape();

function biomeColorTint(biome: string): THREE.Color | null {
  switch (biome) {
    case 'forest':
      return new THREE.Color('#6b8c42');
    case 'desert':
      return new THREE.Color('#d4b86a');
    case 'mountain':
      return new THREE.Color('#8a8a80');
    case 'hills':
      return new THREE.Color('#9ea84a');
    case 'ocean':
    case 'coast':
      return new THREE.Color('#4a80aa');
    case 'river':
      return new THREE.Color('#5599bb');
    case 'tundra':
      // Frost-meadow sage, not near-white: the old #b8c8c8 read as a blank
      // washed-out patch next to plains and forest tiles.
      return new THREE.Color('#a9bfae');
    default:
      return null;
  }
}

// Biomes whose ground is vegetation: these get the lush <-> dry drift and a
// saturation trim (the sim palette + grass texture read lime otherwise).
const VEGETATED = new Set(['plains', 'forest', 'hills']);

/**
 * Per-tile colour variation, deterministic from world position. A shared
 * low-frequency field shifts neighbouring tiles together (lush hollows, dry
 * patches — same idea as the tree palettes in TerrainScatter), and a small
 * per-tile hash keeps adjacent hexes from matching exactly. Mutates `color`.
 */
export function varyTileColor(
  color: THREE.Color,
  biome: string,
  x: number,
  z: number,
): THREE.Color {
  const field = smoothField(x * 0.16 + 3.7, z * 0.16 - 1.9); // 0 lush .. 1 dry
  const jitter = hexNoise(x * 1.37, z * 1.91) * 2 - 1; // -1..1
  color.getHSL(_hsl);
  if (VEGETATED.has(biome)) {
    // Dry patches drift toward straw (hue down), lush ones toward deeper green.
    _hsl.h += (0.5 - field) * 0.035 + jitter * 0.006;
    _hsl.s *= 0.78 - field * 0.12;
    _hsl.l *= 0.94 + (field - 0.5) * 0.2 + jitter * 0.045;
  } else {
    _hsl.h += jitter * 0.004;
    _hsl.l *= 0.96 + (field - 0.5) * 0.1 + jitter * 0.025;
  }
  return color.setHSL(_hsl.h, Math.min(1, _hsl.s), Math.min(1, _hsl.l));
}

/** A land tile's colour before fog: biome tint (or sim colour) plus variation. Mutates `color`. */
export function tileBaseColor(
  tile: SceneNode,
  color: THREE.Color,
  seasonTint?: SeasonTint | null,
): THREE.Color {
  const props = tile.props as Record<string, unknown> | undefined;
  const biome = (props?.biome as string) ?? 'plains';
  const [x, , z] = tile.transform.position;
  color.set(biomeColorTint(biome) ?? tile.visual?.material?.color ?? '#7ea84a');
  varyTileColor(color, biome, x, z);
  // Season lands on top of the per-tile variation. Vegetated biomes take the
  // full tint (the season tables are tuned for them); desert/tundra/mountain
  // take it at reduced weight so dunes and rock don't turn green/orange.
  if (seasonTint) {
    const w = VEGETATED.has(biome) ? 1 : 0.4;
    return applySeasonTint(
      color,
      w >= 1 ? seasonTint : { ...seasonTint, frost: seasonTint.frost * w },
      FROST_COLORS.ground,
    );
  }
  return color;
}

/** "q,r" -> packed base colour (packRGB) of every land tile, for neighbour blends. */
export function buildTileBaseColors(
  tiles: ReadonlyArray<SceneNode>,
  seasonTint?: SeasonTint | null,
): Map<string, number> {
  const out = new Map<string, number>();
  const c = new THREE.Color();
  for (const tile of tiles) {
    const props = tile.props as Record<string, unknown> | undefined;
    const q = props?.q as number | undefined;
    const r = props?.r as number | undefined;
    if (q === undefined || r === undefined) continue;
    tileBaseColor(tile, c, seasonTint);
    out.set(`${q},${r}`, packRGB(c.r, c.g, c.b));
  }
  return out;
}

/** Shoreline colour on a land tile's water-facing edges, per biome. */
